import React from 'react';
import { REPORT_PRINT_THEME, REPORT_PRINT_TYPO } from '../../../utils/dailyReport/reportPrintTheme';

const formatCount = (value) => parseInt(value || 0, 10).toLocaleString();
const formatDepth = (value) =>
  Math.round(Number(value) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const formatPct = (value) => `${(Number(value) || 0).toFixed(1)}%`;

function KpiCell({ label, value, sub, color = REPORT_PRINT_THEME.text }) {
  return (
    <td
      style={{
        padding: '8px 5px',
        textAlign: 'center',
        border: `1px solid ${REPORT_PRINT_THEME.border}`,
        background: REPORT_PRINT_THEME.background,
        verticalAlign: 'middle',
        width: '11%',
      }}
    >
      <div style={{ ...REPORT_PRINT_TYPO.kpiValue, color }}>{value}</div>
      <div
        style={{
          ...REPORT_PRINT_TYPO.kpiLabel,
          color: REPORT_PRINT_THEME.textMedium,
          marginTop: '4px',
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
        }}
      >
        {label}
      </div>
      {sub != null && (
        <div style={{ ...REPORT_PRINT_TYPO.footnote, color: REPORT_PRINT_THEME.textLight, marginTop: '2px' }}>
          {sub}
        </div>
      )}
    </td>
  );
}

function ProgressRow({ label, current, total, pct, color }) {
  const width = Math.min(Number(pct) || 0, 100);
  return (
    <tr>
      <td
        style={{
          padding: '6px 8px',
          border: `1px solid ${REPORT_PRINT_THEME.border}`,
          background: REPORT_PRINT_THEME.backgroundAlt,
          fontSize: '11px',
          fontWeight: 600,
          color: REPORT_PRINT_THEME.textMedium,
          width: '18%',
        }}
      >
        {label}
      </td>
      <td
        style={{
          padding: '6px 8px',
          border: `1px solid ${REPORT_PRINT_THEME.border}`,
          background: REPORT_PRINT_THEME.background,
          width: '62%',
        }}
      >
        <div
          style={{
            height: '16px',
            background: REPORT_PRINT_THEME.backgroundMuted,
            borderRadius: '4px',
            overflow: 'hidden',
            border: `1px solid ${REPORT_PRINT_THEME.border}`,
          }}
        >
          <div
            style={{
              width: `${width}%`,
              height: '100%',
              background: color,
              borderRadius: '3px',
            }}
          />
        </div>
      </td>
      <td
        style={{
          padding: '6px 8px',
          border: `1px solid ${REPORT_PRINT_THEME.border}`,
          background: REPORT_PRINT_THEME.background,
          fontSize: '10px',
          color: REPORT_PRINT_THEME.text,
          textAlign: 'right',
          whiteSpace: 'nowrap',
          width: '20%',
        }}
      >
        {formatDepth(current)} / {formatDepth(total)} m ({formatPct(pct)})
      </td>
    </tr>
  );
}

function SampleProgressRow({ label, current, total, pct, color }) {
  const width = Math.min(Number(pct) || 0, 100);
  return (
    <tr>
      <td
        style={{
          padding: '6px 8px',
          border: `1px solid ${REPORT_PRINT_THEME.border}`,
          background: REPORT_PRINT_THEME.backgroundAlt,
          fontSize: '11px',
          fontWeight: 600,
          color: REPORT_PRINT_THEME.textMedium,
          width: '18%',
        }}
      >
        {label}
      </td>
      <td
        style={{
          padding: '6px 8px',
          border: `1px solid ${REPORT_PRINT_THEME.border}`,
          background: REPORT_PRINT_THEME.background,
          width: '62%',
        }}
      >
        <div
          style={{
            height: '16px',
            background: REPORT_PRINT_THEME.backgroundMuted,
            borderRadius: '4px',
            overflow: 'hidden',
            border: `1px solid ${REPORT_PRINT_THEME.border}`,
          }}
        >
          <div
            style={{
              width: `${width}%`,
              height: '100%',
              background: color,
              borderRadius: '3px',
            }}
          />
        </div>
      </td>
      <td
        style={{
          padding: '6px 8px',
          border: `1px solid ${REPORT_PRINT_THEME.border}`,
          background: REPORT_PRINT_THEME.background,
          fontSize: '10px',
          color: REPORT_PRINT_THEME.text,
          textAlign: 'right',
          whiteSpace: 'nowrap',
          width: '20%',
        }}
      >
        {formatCount(current)} / {formatCount(total)} ({formatPct(pct)})
      </td>
    </tr>
  );
}

const PrintProgramMetrics = ({ drillingMetrics, sampleMetrics }) => {
  if (!drillingMetrics) {
    return (
      <div style={{ width: '100%', padding: '20px', background: REPORT_PRINT_THEME.background }}>
        <p style={{ color: REPORT_PRINT_THEME.text }}>No drilling metrics available.</p>
      </div>
    );
  }

  const designated = sampleMetrics?.designated ?? 0;

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        background: REPORT_PRINT_THEME.background,
        boxSizing: 'border-box',
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        <thead>
          <tr>
            <th
              colSpan={9}
              style={{
                padding: '6px 8px',
                textAlign: 'left',
                background: REPORT_PRINT_THEME.headerBg,
                color: REPORT_PRINT_THEME.headerText,
                border: `1px solid ${REPORT_PRINT_THEME.borderDark}`,
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.03em',
                textTransform: 'uppercase',
              }}
            >
              Drilling Overview
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <KpiCell label="Total Holes" value={formatCount(drillingMetrics.totalHoles)} color={REPORT_PRINT_THEME.accentDark} />
            <KpiCell label="Complete" value={formatCount(drillingMetrics.completedHoles)} color={REPORT_PRINT_THEME.success} />
            <KpiCell label="In Progress" value={formatCount(drillingMetrics.inProgressHoles)} color={REPORT_PRINT_THEME.danger} />
            <KpiCell label="Avg Drilled" value={formatPct(drillingMetrics.avgDrilledPct)} color={REPORT_PRINT_THEME.success} />
            <KpiCell label="Avg Logged" value={formatPct(drillingMetrics.avgLoggedPct)} color={REPORT_PRINT_THEME.info} />
            <KpiCell label="Designated" value={formatCount(sampleMetrics?.designated)} color={REPORT_PRINT_THEME.purple} />
            <KpiCell label="Bagged" value={formatCount(sampleMetrics?.bagged)} sub={sampleMetrics ? formatPct(sampleMetrics.pctBagged) : null} color={REPORT_PRINT_THEME.warning} />
            <KpiCell label="At Lab" value={formatCount(sampleMetrics?.atLab)} sub={sampleMetrics ? formatPct(sampleMetrics.pctAtLab) : null} color={REPORT_PRINT_THEME.info} />
            <KpiCell label="Finalized" value={formatCount(sampleMetrics?.finalized)} sub={sampleMetrics ? formatPct(sampleMetrics.pctFinalized) : null} color={REPORT_PRINT_THEME.purple} />
          </tr>
        </tbody>
      </table>

      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
        }}
      >
        <thead>
          <tr>
            <th
              colSpan={3}
              style={{
                padding: '6px 8px',
                textAlign: 'left',
                background: REPORT_PRINT_THEME.headerBg,
                color: REPORT_PRINT_THEME.headerText,
                border: `1px solid ${REPORT_PRINT_THEME.borderDark}`,
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.03em',
                textTransform: 'uppercase',
              }}
            >
              Program Progress — Target {formatDepth(drillingMetrics.targetDepthM)} m
            </th>
          </tr>
        </thead>
        <tbody>
          <ProgressRow
            label="Total Drilled"
            current={drillingMetrics.drilledDepthM}
            total={drillingMetrics.targetDepthM}
            pct={drillingMetrics.drilledPct}
            color={REPORT_PRINT_THEME.warning}
          />
          <ProgressRow
            label="Total Logged"
            current={drillingMetrics.loggedDepthM}
            total={drillingMetrics.targetDepthM}
            pct={drillingMetrics.loggedPct}
            color={REPORT_PRINT_THEME.success}
          />
          {drillingMetrics.usingDrillzProgress && (
            <ProgressRow
              label="Total Cut"
              current={drillingMetrics.cutDepthM}
              total={drillingMetrics.targetDepthM}
              pct={drillingMetrics.cutPct}
              color={REPORT_PRINT_THEME.indigo}
            />
          )}
        </tbody>
      </table>

      {sampleMetrics && designated > 0 && (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            <tr>
              <th
                colSpan={3}
                style={{
                  padding: '6px 8px',
                  textAlign: 'left',
                  background: REPORT_PRINT_THEME.headerBg,
                  color: REPORT_PRINT_THEME.headerText,
                  border: `1px solid ${REPORT_PRINT_THEME.borderDark}`,
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                }}
              >
                Sample Processing
              </th>
            </tr>
          </thead>
          <tbody>
            <SampleProgressRow label="Bagged" current={sampleMetrics.bagged} total={designated} pct={sampleMetrics.pctBagged} color={REPORT_PRINT_THEME.warning} />
            <SampleProgressRow label="At Lab" current={sampleMetrics.atLab} total={designated} pct={sampleMetrics.pctAtLab} color={REPORT_PRINT_THEME.info} />
            <SampleProgressRow label="Finalized" current={sampleMetrics.finalized} total={designated} pct={sampleMetrics.pctFinalized} color={REPORT_PRINT_THEME.purple} />
            <SampleProgressRow label="QAQC Passed" current={sampleMetrics.qaqcPassed} total={designated} pct={sampleMetrics.pctQaqcPassed} color={REPORT_PRINT_THEME.success} />
          </tbody>
        </table>
      )}

      {drillingMetrics.usingDrillzProgress && (
        <p
          style={{
            ...REPORT_PRINT_TYPO.footnote,
            color: REPORT_PRINT_THEME.textLight,
            marginTop: '12px',
            marginBottom: 0,
          }}
        >
          Drilled, logged, and cut totals sourced from Drillz campaign progress where linked.
        </p>
      )}
    </div>
  );
};

export default PrintProgramMetrics;
